// ============================================
// runRealtimeEvent — Realtime data-channel dispatcher
// ============================================
// Wave 4 finish: full extraction of the inline switch from
// VoiceBotPanel.tsx `handleDataChannelMessage`.
//
// Behavior preservation rules:
// - All side-effects (state setters, ref mutations, dataChannel.send,
//   setTimeout, addMessage, triggerResponse, endSession) are passed
//   in via the `deps` bag and invoked in the exact same order as the
//   original inline code.
// - The caller still owns:
//     * try/catch around JSON.parse(event.data)
//     * try/catch around the dispatcher itself
//     * the outer useCallback wrapper (so React deps semantics remain
//       identical to the inline version)
//     * the `transcriptionsToSkipRef` decrement on the very first
//       branch.
// - `pendingReminder` / `pendingCallEnd` are passed as REFS
//   (`pendingReminderRef` / `pendingCallEndRef`) so the dispatcher reads
//   their CURRENT value. The owning useCallback captures plain state values
//   stale (they are not in its dep array), which previously broke
//   goodbye-driven auto-close: the response-done branch read a stale
//   `pendingCallEnd === false` and never ended the session.

import type { MutableRefObject } from 'react'
import { CALENDLY_SYSTEM_MESSAGES, type LanguageCode } from '../config/language'
import { TIMING } from '../constants/voice-bot-timing'
import {
  detectUserGoodbye,
  detectBotCallEnd,
  extractSpokenUrlFragments,
  isInternalRealtimeApiWarning,
  shouldRecoverInsuranceNoInfo,
} from './realtime-event-helpers'
import { botReplyConfirmsEnd } from '../utils/goodbye-close'
import { runInsuranceNoInfoRecovery } from './run-insurance-no-info-recovery'
import type { ScheduledMeeting, VoiceSessionData } from '../types'

export interface RunRealtimeEventDeps {
  // Refs (read/write)
  transcriptionsToSkipRef: MutableRefObject<number>
  dataChannelRef: MutableRefObject<RTCDataChannel | null>
  initialGreetingCompleteRef: MutableRefObject<boolean>
  lastUserMessageRef: MutableRefObject<string | null>
  lastActivityTime: MutableRefObject<number>
  selectedLanguageRef: MutableRefObject<LanguageCode>
  scheduledMeetingRef: MutableRefObject<ScheduledMeeting | null>
  showCalendlyEmbedRef: MutableRefObject<boolean>
  bookingConfirmedRef: MutableRefObject<boolean>
  goodbyeSentRef: MutableRefObject<boolean>
  currentBotResponse: MutableRefObject<string>
  responseHadAudioRef: MutableRefObject<boolean>
  pendingLinksRef: MutableRefObject<Array<{ label: string; url: string }> | null>
  sessionDataRef: MutableRefObject<VoiceSessionData | null>
  insuranceRecoveryForMessageRef: MutableRefObject<string | null>
  greetingRetryTimeoutRef: MutableRefObject<ReturnType<typeof setTimeout> | null>
  pendingEndAfterSpeakingRef: MutableRefObject<boolean>
  peerConnectionRef: MutableRefObject<RTCPeerConnection | null>
  pendingGreetingUnmuteRef: MutableRefObject<boolean>
  preGreetingAudioCommitsRef: MutableRefObject<number>
  activeResponseRef: MutableRefObject<boolean>
  responseStallTimerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>
  pendingResponseRef: MutableRefObject<boolean>
  tokensUsedRef: MutableRefObject<number>
  currentUserTranscriptRef: MutableRefObject<string>
  currentUserTranscriptItemRef: MutableRefObject<string | null>
  committedUserTranscriptItemsRef: MutableRefObject<Set<string>>
  userTranscriptFlushTimerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>

  // State setters
  setPendingCallEnd: (v: boolean) => void
  setPendingReminder: (v: boolean) => void
  setPendingEndAfterSpeaking: (v: boolean) => void
  setIsSpeaking: (v: boolean) => void
  setMicrophoneForBotSpeaking: (v: boolean) => void
  setIsMuted: (v: boolean) => void
  setError: (v: string | null) => void
  setUserPartial: (v: string) => void

  // Closures
  addMessage: (role: 'user' | 'assistant', text: string, links?: Array<{ label: string; url: string }>) => void
  triggerResponse: (label: string, force?: boolean) => void
  handleFunctionCall: (callId: string, name: string, args: Record<string, unknown>) => Promise<unknown>
  endSession: () => void

  // Ref-backed so reads are always current. The owning useCallback captures
  // plain state values stale, which previously broke goodbye auto-close.
  pendingReminderRef: MutableRefObject<boolean>
  pendingCallEndRef: MutableRefObject<boolean>
  publicKey: string | undefined
}

export const runRealtimeEvent = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  message: any,
  deps: RunRealtimeEventDeps,
): void => {
  const {
    transcriptionsToSkipRef,
    dataChannelRef,
    initialGreetingCompleteRef,
    lastUserMessageRef,
    lastActivityTime,
    selectedLanguageRef,
    scheduledMeetingRef,
    showCalendlyEmbedRef,
    bookingConfirmedRef,
    goodbyeSentRef,
    currentBotResponse,
    responseHadAudioRef,
    pendingLinksRef,
    sessionDataRef,
    insuranceRecoveryForMessageRef,
    greetingRetryTimeoutRef,
    pendingEndAfterSpeakingRef,
    peerConnectionRef,
    pendingGreetingUnmuteRef,
    preGreetingAudioCommitsRef,
    activeResponseRef,
    responseStallTimerRef,
    pendingResponseRef,
    tokensUsedRef,
    currentUserTranscriptRef,
    currentUserTranscriptItemRef,
    committedUserTranscriptItemsRef,
    userTranscriptFlushTimerRef,
    setPendingCallEnd,
    setPendingReminder,
    setPendingEndAfterSpeaking,
    setIsSpeaking,
    setMicrophoneForBotSpeaking,
    setIsMuted,
    setError,
    setUserPartial,
    addMessage,
    triggerResponse,
    handleFunctionCall,
    endSession,
    pendingReminderRef,
    pendingCallEndRef,
    publicKey,
  } = deps

  const commitUserTranscript = (rawText: string, itemId: string | null): void => {
    if (userTranscriptFlushTimerRef.current) {
      clearTimeout(userTranscriptFlushTimerRef.current)
      userTranscriptFlushTimerRef.current = null
    }

    const userMsg = rawText.trim()
    currentUserTranscriptRef.current = ''
    currentUserTranscriptItemRef.current = null
    setUserPartial('')
    if (!userMsg) return

    if (itemId && committedUserTranscriptItemsRef.current.has(itemId)) return
    if (itemId) {
      committedUserTranscriptItemsRef.current.add(itemId)
      if (committedUserTranscriptItemsRef.current.size > 50) {
        const oldest = committedUserTranscriptItemsRef.current.values().next().value
        if (oldest) committedUserTranscriptItemsRef.current.delete(oldest)
      }
    }

    if (transcriptionsToSkipRef.current > 0) {
      transcriptionsToSkipRef.current--
      console.log(
        '🚫 Skipping pre-greeting transcription:',
        userMsg,
        '(remaining to skip:',
        transcriptionsToSkipRef.current,
        ')',
      )
      if (dataChannelRef.current?.readyState === 'open' && initialGreetingCompleteRef.current) {
        dataChannelRef.current.send(JSON.stringify({ type: 'response.cancel' }))
        console.log('🛑 Cancelled auto-response from pre-greeting audio')
      } else {
        console.log('⚠️ Skipping response.cancel - initial greeting still in progress')
      }
      return
    }

    lastUserMessageRef.current = userMsg
    addMessage('user', userMsg)
    lastActivityTime.current = Date.now()

    const userSaidGoodbye = detectUserGoodbye(userMsg, selectedLanguageRef.current)
    const hasScheduledMeeting = !!scheduledMeetingRef.current
    const isCalendlyOpen = showCalendlyEmbedRef.current
    const isBookingConfirmed = bookingConfirmedRef.current

    console.log('🔍 User goodbye check:', {
      userMsg,
      userSaidGoodbye,
      hasScheduledMeeting,
      isBookingConfirmed,
      isCalendlyOpen,
    })

    if (userSaidGoodbye && hasScheduledMeeting && !isBookingConfirmed && isCalendlyOpen) {
      console.log('⏳ User said goodbye but booking not confirmed - will remind after bot finishes...')
      setPendingCallEnd(true)
      setPendingReminder(true)
    } else if (userSaidGoodbye && (!hasScheduledMeeting || isBookingConfirmed)) {
      console.log('✅ User goodbye - will close after bot responds')
      setPendingCallEnd(true)
    }
  }

  switch (message.type) {
    case 'conversation.item.input_audio_transcription.delta': {
      const delta = typeof message.delta === 'string' ? message.delta : ''
      if (!delta) break

      const itemId =
        typeof message.item_id === 'string'
          ? message.item_id
          : currentUserTranscriptItemRef.current
      currentUserTranscriptItemRef.current = itemId
      currentUserTranscriptRef.current += delta
      setUserPartial(currentUserTranscriptRef.current.trimStart())

      if (userTranscriptFlushTimerRef.current) {
        clearTimeout(userTranscriptFlushTimerRef.current)
      }
      userTranscriptFlushTimerRef.current = setTimeout(() => {
        commitUserTranscript(currentUserTranscriptRef.current, currentUserTranscriptItemRef.current)
      }, 1200)
      break
    }

    // User speech transcription
    case 'conversation.item.input_audio_transcription.completed': {
      const itemId =
        typeof message.item_id === 'string'
          ? message.item_id
          : currentUserTranscriptItemRef.current
      const transcript =
        typeof message.transcript === 'string' && message.transcript.trim()
          ? message.transcript
          : currentUserTranscriptRef.current
      commitUserTranscript(transcript, itemId)
      break
    }

    // Bot response (accumulate deltas) - GA Protocol event name
    case 'response.output_audio_transcript.delta':
      currentBotResponse.current += message.delta
      responseHadAudioRef.current = true
      break

    // Bot response complete - GA Protocol event name
    case 'response.output_audio_transcript.done':
      if (currentBotResponse.current.trim()) {
        const botMsg = currentBotResponse.current
        console.log('🤖 Bot full audio transcript:', botMsg)
        console.log('🤖 Transcript length:', botMsg.length, 'chars')
        const urlFragments = extractSpokenUrlFragments(botMsg)
        if (urlFragments.length > 0) {
          console.warn('⚠️ Bot spoke URL fragments:', urlFragments.join(', '))
        }
        const pendingLinks = pendingLinksRef.current
        if (pendingLinks?.length) {
          pendingLinksRef.current = null
          console.log('🔗 Attaching', pendingLinks.length, 'travel links to bot message')
          addMessage('assistant', botMsg, pendingLinks)
        } else {
          addMessage('assistant', botMsg)
        }
        currentBotResponse.current = ''
        lastActivityTime.current = Date.now()

        const customerIndustry = sessionDataRef.current?.industry || 'real_estate'

        // Residents (Dona): auto-hang-up when the BOT itself signals the call is
        // over (says goodbye / "השיחה הסתיימה"), instead of waiting for the user
        // to hang up. Gated to residents so every other voice customer's flow is
        // byte-identical. Closes after the farewell audio finishes playing
        // (output_audio_buffer.stopped → endSession), reusing the existing
        // pendingEndAfterSpeaking path.
        if (
          customerIndustry === 'residents' &&
          !pendingEndAfterSpeakingRef.current &&
          detectBotCallEnd(botMsg, selectedLanguageRef.current)
        ) {
          console.log('📞 Bot signaled call end (residents) - will close after audio finishes')
          setPendingEndAfterSpeaking(true)
        }

        const shouldRecoverInsurance = shouldRecoverInsuranceNoInfo(
          customerIndustry,
          botMsg,
          lastUserMessageRef.current,
          insuranceRecoveryForMessageRef.current,
        )

        if (shouldRecoverInsurance) {
          const userQuestion = lastUserMessageRef.current
          insuranceRecoveryForMessageRef.current = userQuestion
          void runInsuranceNoInfoRecovery({
            userQuestion: userQuestion as string,
            publicKey,
            sessionDataRef,
            dataChannelRef,
            triggerResponse,
          })
        }

        if (pendingReminderRef.current) {
          console.log('📩 Sending booking reminder to bot...')
          setPendingReminder(false)
          if (dataChannelRef.current?.readyState === 'open') {
            const reminderLang = selectedLanguageRef.current
            const reminderMessages = CALENDLY_SYSTEM_MESSAGES[reminderLang] || CALENDLY_SYSTEM_MESSAGES['he']
            dataChannelRef.current.send(JSON.stringify({
              type: 'conversation.item.create',
              item: {
                type: 'message',
                role: 'user',
                content: [{ type: 'input_text', text: reminderMessages.bookingReminder }],
              },
            }))
            triggerResponse('booking-reminder')
            setPendingCallEnd(false)
          }
        } else if (pendingCallEndRef.current) {
          const hasScheduledMeeting = !!scheduledMeetingRef.current
          const isBookingConfirmed = bookingConfirmedRef.current

          if (!hasScheduledMeeting || isBookingConfirmed) {
            console.log('✅ Bot should say goodbye now...')
            const meetingSource = scheduledMeetingRef.current?.source
            if (dataChannelRef.current?.readyState === 'open' && hasScheduledMeeting && isBookingConfirmed && !goodbyeSentRef.current && meetingSource !== 'verbal') {
              goodbyeSentRef.current = true
              console.log('📅 Meeting confirmed - sending goodbye instruction to bot')
              const goodbyeLang = selectedLanguageRef.current
              const goodbyeMessages = CALENDLY_SYSTEM_MESSAGES[goodbyeLang] || CALENDLY_SYSTEM_MESSAGES['he']
              dataChannelRef.current.send(JSON.stringify({
                type: 'conversation.item.create',
                item: {
                  type: 'message',
                  role: 'user',
                  content: [{ type: 'input_text', text: goodbyeMessages.confirmedGoodbye }],
                },
              }))
              triggerResponse('meeting-confirmed-goodbye')
              console.log('⏳ Will close session after bot finishes speaking')
              setPendingEndAfterSpeaking(true)
            } else {
              // User "goodbye" detected, but only end if the bot's own reply
              // confirms the call is over. If the bot is still engaging (it
              // asked the user a question), the goodbye was likely an ASR
              // mistranscription (e.g. a request for more info heard as
              // "ביי ביי") — keep the call open instead of hanging up mid-chat.
              if (botReplyConfirmsEnd(botMsg)) {
                console.log('🛑 No meeting - will close after bot finishes speaking')
                setPendingEndAfterSpeaking(true)
              } else {
                console.log('↩️ Bot still engaging (asked a question) after a detected goodbye - NOT closing; likely a mistranscribed farewell')
              }
            }
            setPendingCallEnd(false)
          } else {
            console.log('⏳ Waiting for booking confirmation...')
            setPendingCallEnd(false)
          }
        }
      }
      break

    case 'output_audio_buffer.cleared':
      console.log('🔇 Bot audio cleared (user interrupted)')
      break

    case 'output_audio_buffer.started':
      setIsSpeaking(true)
      setMicrophoneForBotSpeaking(true)
      responseHadAudioRef.current = true
      if (greetingRetryTimeoutRef.current) {
        clearTimeout(greetingRetryTimeoutRef.current)
        greetingRetryTimeoutRef.current = null
      }
      break

    case 'output_audio_buffer.stopped':
      setIsSpeaking(false)
      setMicrophoneForBotSpeaking(false)

      if (pendingEndAfterSpeakingRef.current) {
        console.log('🛑 Bot finished speaking - closing session now')
        setPendingEndAfterSpeaking(false)
        setTimeout(() => endSession(), TIMING.SESSION_END_AUDIO_DELAY_MS)
      }

      if (pendingGreetingUnmuteRef.current) {
        pendingGreetingUnmuteRef.current = false
        if (peerConnectionRef.current) {
          peerConnectionRef.current.getSenders().forEach((sender) => {
            if (sender.track?.kind === 'audio') {
              sender.track.enabled = true
              console.log('🎤 Microphone track ENABLED after greeting')
            }
          })
        }
        setIsMuted(false)
        console.log('🎤 Initial greeting AUDIO complete - user can speak now')
      }
      break

    case 'input_audio_buffer.speech_started':
      setIsSpeaking(false)
      break

    case 'input_audio_buffer.committed':
      if (!initialGreetingCompleteRef.current) {
        preGreetingAudioCommitsRef.current++
        transcriptionsToSkipRef.current++
        console.log('⚠️ Audio committed before greeting complete - will skip transcription #', preGreetingAudioCommitsRef.current)
      }
      break

    case 'response.function_call_arguments.done':
      try {
        const parsedArgs = JSON.parse(message.arguments || '{}')
        void handleFunctionCall(message.call_id, message.name, parsedArgs)
      } catch (parseError) {
        console.error('❌ Failed to parse function call arguments (possibly interrupted):', parseError)
        console.log('   Raw arguments:', message.arguments)
        if (dataChannelRef.current?.readyState === 'open' && message.call_id) {
          dataChannelRef.current.send(JSON.stringify({
            type: 'conversation.item.create',
            item: {
              type: 'function_call_output',
              call_id: message.call_id,
              output: JSON.stringify({ error: 'Function was interrupted. Please try again or ask the customer to repeat.' }),
            },
          }))
          triggerResponse('function-parse-error-recovery', true)
        }
      }
      break

    case 'error': {
      console.error('Session error:', message.error)
      const errorMsg = message.error?.message || ''
      if (!isInternalRealtimeApiWarning(errorMsg)) {
        setError(errorMsg || 'Unknown error')
      }
      break
    }

    case 'session.created':
      console.log('✅ Session created')
      break

    case 'session.updated':
      console.log('✅ Session configured')
      break

    case 'response.created':
      activeResponseRef.current = true
      responseHadAudioRef.current = false
      if (responseStallTimerRef.current) {
        clearTimeout(responseStallTimerRef.current)
      }
      responseStallTimerRef.current = setTimeout(() => {
        if (activeResponseRef.current && !responseHadAudioRef.current) {
          console.warn('⏱️ Response stall detected (no audio). Recovering state...')
          activeResponseRef.current = false
          if (pendingLinksRef.current?.length) {
            const orphanedLinks = pendingLinksRef.current
            pendingLinksRef.current = null
            console.log('🔗 Flushing orphaned links during stall recovery:', orphanedLinks.length)
            addMessage('assistant', '', orphanedLinks)
          }
          if (pendingResponseRef.current) {
            pendingResponseRef.current = false
            triggerResponse('response-stall-recovery', true)
          }
        }
      }, TIMING.RESPONSE_STALL_TIMEOUT_MS)
      break

    case 'response.done':
      console.log('📥 Response finished')
      if (responseStallTimerRef.current) {
        clearTimeout(responseStallTimerRef.current)
        responseStallTimerRef.current = null
      }
      activeResponseRef.current = false

      if (message.response?.usage) {
        tokensUsedRef.current += (message.response.usage.total_tokens || 0)
      }

      if (pendingLinksRef.current?.length && !responseHadAudioRef.current) {
        const orphanedLinks = pendingLinksRef.current
        pendingLinksRef.current = null
        console.log('🔗 Flushing orphaned links as standalone message:', orphanedLinks.length)
        addMessage('assistant', '', orphanedLinks)
      }

      if (pendingResponseRef.current && dataChannelRef.current?.readyState === 'open') {
        pendingResponseRef.current = false
        setTimeout(() => {
          triggerResponse('pending-queue', true)
        }, TIMING.PENDING_QUEUE_DELAY_MS)
      }

      if (!initialGreetingCompleteRef.current) {
        initialGreetingCompleteRef.current = true
        pendingGreetingUnmuteRef.current = true
        console.log('🎤 Initial greeting text complete - waiting for audio to finish before unmuting')
        console.log('   Will skip', transcriptionsToSkipRef.current, 'pre-greeting transcriptions')
      }
      break
  }
}
